import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Calendar, List, Check, RotateCcw, Ban, CheckCircle2, Camera, Image as ImageIcon, Undo2, Send, Plus } from "lucide-react";
import { api, apiErr, Badge, inputCls, labelCls } from "./adminShared";

function ProofThumb({ path }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked;
    api.get(`/files/${path}`, { responseType: "blob" })
      .then((r) => { const u = URL.createObjectURL(r.data); revoked = u; setUrl(u); }).catch(() => {});
    return () => revoked && URL.revokeObjectURL(revoked);
  }, [path]);
  if (!url) return <div className="w-full h-20 bg-[#F5F0E8] rounded-lg flex items-center justify-center"><ImageIcon className="text-[#B87333]/40" size={18} /></div>;
  return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="proof" className="w-full h-20 object-cover rounded-lg border border-[#E6DEC3]" /></a>;
}

function BookingModal({ b, onClose, onChanged }) {
  const [resched, setResched] = useState({ date: b.date, start_time: b.start_time });
  const [photos, setPhotos] = useState(b.proof_photos || []);
  const [proofLabel, setProofLabel] = useState("before");
  const [uploading, setUploading] = useState(false);
  const setStatus = async (status) => {
    try { const { data } = await api.patch(`/admin/bookings/${b.booking_id}/status`, { status }); toast.success(`Marked ${status}${data.recurring_booking ? ` · next recurring booking ${data.recurring_booking} created` : ""}`); onChanged(); onClose(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const markComplete = async () => {
    try {
      const { data } = await api.post(`/admin/bookings/${b.booking_id}/complete`);
      toast.success(data.status === "balance_due" ? "Marked complete · balance request emailed to customer" : "Marked complete & paid");
      onChanged(); onClose();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const resendBalance = async () => {
    try { await api.post(`/admin/bookings/${b.booking_id}/resend-balance`); toast.success("Balance request re-sent to customer"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doRefund = async (kind) => {
    if (!window.confirm(`Refund the ${kind}? This returns the money to the customer via Stripe.`)) return;
    try { await api.post(`/admin/bookings/${b.booking_id}/refund`, { kind }); toast.success(`${kind[0].toUpperCase() + kind.slice(1)} refunded`); onChanged(); onClose(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const uploadProof = async (e) => {
    const files = Array.from(e.target.files); if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("label", proofLabel);
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/admin/bookings/${b.booking_id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos(data.proof_photos); toast.success("Photos added"); onChanged();
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); }
  };
  const doReschedule = async () => {
    try { await api.patch(`/admin/bookings/${b.booking_id}/reschedule`, resched); toast.success("Rescheduled"); onChanged(); onClose(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="booking-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-[#B87333] font-semibold">{b.booking_number}</p>
            <h3 className="font-serif text-2xl text-[#2C2621] mt-1">{b.service_name}</h3>
          </div>
          <button onClick={onClose} data-testid="modal-close"><X className="text-[#635B52]" /></button>
        </div>
        <div className="mt-4 space-y-1.5 text-sm text-[#635B52]">
          <p><strong className="text-[#2C2621]">Status:</strong> <Badge s={b.status} /></p>
          <p><strong className="text-[#2C2621]">Size:</strong> {b.tier_label}</p>
          <p><strong className="text-[#2C2621]">When:</strong> {b.date} at {b.start_time} CT ({b.duration_minutes} min)</p>
          <p><strong className="text-[#2C2621]">Total:</strong> ${b.total_price.toFixed(2)} <span className="text-xs">(deposit {b.deposit_paid ? "paid" : "unpaid"}, balance {b.balance_paid ? "paid" : "unpaid"})</span></p>
          <p><strong className="text-[#2C2621]">Customer:</strong> {b.customer_name} · {b.customer_email} · {b.customer_phone}</p>
          <p><strong className="text-[#2C2621]">Address:</strong> {b.address}</p>
          {b.property_instructions && <p><strong className="text-[#2C2621]">Property:</strong> {b.property_instructions}</p>}
          {b.access_instructions && <p><strong className="text-[#2C2621]">Access:</strong> {b.access_instructions}</p>}
          {b.parking_info && <p><strong className="text-[#2C2621]">Parking:</strong> {b.parking_info}</p>}
          {b.client_request && <p className="text-[#B87333]"><strong>Client requested:</strong> {b.client_request.action} — {b.client_request.message}</p>}
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <button onClick={() => setStatus("confirmed")} className="inline-flex items-center gap-1 bg-green-600 text-white rounded-full px-4 py-2 text-sm" data-testid="btn-confirm"><Check size={14} /> Confirm</button>
          <button onClick={markComplete} className="inline-flex items-center gap-1 bg-[#2C2621] text-white rounded-full px-4 py-2 text-sm" data-testid="btn-complete"><CheckCircle2 size={14} /> Mark Cleaning Complete</button>
          <button onClick={() => setStatus("cancelled")} className="inline-flex items-center gap-1 bg-red-600 text-white rounded-full px-4 py-2 text-sm" data-testid="btn-cancel"><Ban size={14} /> Cancel</button>
        </div>

        <div className="mt-5 border-t border-[#E6DEC3] pt-4">
          <p className={labelCls}>Payments</p>
          <div className="mt-2 rounded-xl bg-[#F5F0E8] p-3 text-sm text-[#635B52] space-y-1" data-testid="payment-details">
            {b.subtotal != null && <div className="flex justify-between"><span>Subtotal</span><span className="text-[#2C2621]">${((b.subtotal || 0) + (b.discount_amount || 0)).toFixed(2)}</span></div>}
            {(b.discount_amount || 0) > 0 && <div className="flex justify-between"><span>Discount{b.discount_code ? ` (${b.discount_code})` : ""}</span><span className="text-green-700">−${(b.discount_amount || 0).toFixed(2)}</span></div>}
            {(b.tax_amount || 0) > 0 && <div className="flex justify-between"><span>{b.tax_label || "Texas Sales Tax"}</span><span className="text-[#2C2621]">${(b.tax_amount || 0).toFixed(2)}</span></div>}
            {(b.tax_amount || 0) > 0 && <div className="flex justify-between font-medium"><span className="text-[#2C2621]">Grand total</span><span className="text-[#2C2621]">${(b.total_price || 0).toFixed(2)}</span></div>}
            {(b.tip_amount || 0) > 0 && <div className="flex justify-between"><span>Crew tip</span><span className="text-[#2C2621]">${(b.tip_amount || 0).toFixed(2)}</span></div>}
            <div className="flex justify-between"><span>Deposit ({b.deposit_paid ? "paid" : "unpaid"})</span><span className="text-[#2C2621]">${(b.deposit_amount || 0).toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Balance ({b.balance_paid ? "paid" : "unpaid"})</span><span className="text-[#2C2621]">${(b.balance_amount || 0).toFixed(2)}</span></div>
            <div className="flex justify-between border-t border-[#E6DEC3] pt-1 font-semibold"><span className="text-[#2C2621]">Total paid</span><span className="text-[#2C2621]" data-testid="payment-total-paid">${(((b.deposit_paid ? (b.deposit_amount || 0) : 0) + (b.balance_paid ? (b.balance_amount || 0) : 0))).toFixed(2)} / ${(b.total_price || 0).toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Payment status</span><span className="text-[#2C2621]">{(b.payment_status || "unpaid").replace(/_/g, " ")}</span></div>
            {b.deposit_payment_intent_id && <div className="flex justify-between gap-2"><span>Deposit ID</span><span className="text-[#2C2621] text-xs break-all text-right">{b.deposit_payment_intent_id}</span></div>}
            {b.balance_payment_intent_id && <div className="flex justify-between gap-2"><span>Balance ID</span><span className="text-[#2C2621] text-xs break-all text-right">{b.balance_payment_intent_id}</span></div>}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {!b.balance_paid && (b.balance_amount || 0) > 0 && (
              <button onClick={resendBalance} className="inline-flex items-center gap-1 bg-[#B87333] text-white rounded-full px-4 py-2 text-sm" data-testid="btn-resend-balance"><Send size={14} /> Resend Balance Request</button>
            )}
            {b.deposit_paid && (
              <button onClick={() => doRefund("deposit")} className="inline-flex items-center gap-1 border border-red-300 text-red-700 rounded-full px-4 py-2 text-sm" data-testid="btn-refund-deposit"><Undo2 size={14} /> Refund deposit</button>
            )}
            {b.balance_paid && (
              <button onClick={() => doRefund("balance")} className="inline-flex items-center gap-1 border border-red-300 text-red-700 rounded-full px-4 py-2 text-sm" data-testid="btn-refund-balance"><Undo2 size={14} /> Refund balance</button>
            )}
          </div>
        </div>
        <div className="mt-5 border-t border-[#E6DEC3] pt-4">
          <p className={labelCls}>Reschedule</p>
          <div className="mt-2 flex gap-2">
            <input type="date" value={resched.date} onChange={(e) => setResched({ ...resched, date: e.target.value })} className={inputCls} data-testid="resched-date" />
            <input type="time" value={resched.start_time} onChange={(e) => setResched({ ...resched, start_time: e.target.value })} className={inputCls} data-testid="resched-time" />
            <button onClick={doReschedule} className="inline-flex items-center gap-1 bg-blue-600 text-white rounded-full px-4 py-2 text-sm whitespace-nowrap" data-testid="btn-reschedule"><RotateCcw size={14} /> Move</button>
          </div>
        </div>
        <div className="mt-5 border-t border-[#E6DEC3] pt-4">
          <p className={labelCls}>Before / after photos (owner only)</p>
          <div className="mt-2 flex items-center gap-2">
            <select value={proofLabel} onChange={(e) => setProofLabel(e.target.value)} className={`${inputCls} w-28`} data-testid="proof-label">
              <option value="before">Before</option>
              <option value="after">After</option>
            </select>
            <label className="inline-flex items-center gap-1 bg-[#B87333] text-white rounded-full px-4 py-2 text-sm cursor-pointer" data-testid="proof-upload">
              <Camera size={14} /> {uploading ? "Uploading…" : "Add photos"}
              <input type="file" accept="image/*" multiple className="hidden" onChange={uploadProof} />
            </label>
          </div>
          {photos.length > 0 && (
            <div className="mt-3 grid grid-cols-3 gap-2" data-testid="proof-photos">
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <ProofThumb path={p.path} />
                  <span className="absolute top-1 left-1 text-[10px] bg-[#2C2621] text-white px-1.5 py-0.5 rounded uppercase">{p.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NewAppointmentModal({ onClose, onCreated }) {
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    service_slug: "", tier_id: "", address: "", date: "", start_time: "10:00",
    customer_name: "", customer_email: "", customer_phone: "", property_instructions: "",
    deposit_handling: "pay_link",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/services").then((r) => setServices((r.data || []).filter((s) => s.bookable !== false))).catch(() => {}); }, []);
  const svc = services.find((s) => s.slug === form.service_slug);
  const tiers = svc?.tiers || [];
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.service_slug || !form.tier_id || !form.date || !form.customer_name || !form.customer_email) {
      toast.error("Please fill in service, size, date, name and email"); return;
    }
    setSaving(true);
    try { await api.post("/admin/bookings", form); toast.success("Appointment created"); onCreated(); onClose(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="new-appointment-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h3 className="font-serif text-2xl text-[#2C2621]">New appointment</h3>
          <button onClick={onClose} data-testid="new-appt-close"><X className="text-[#635B52]" /></button>
        </div>
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><p className={labelCls}>Service</p>
              <select value={form.service_slug} onChange={(e) => { set("service_slug", e.target.value); set("tier_id", ""); }} className={inputCls} data-testid="new-appt-service">
                <option value="">Select…</option>
                {services.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
              </select></div>
            <div><p className={labelCls}>Property size</p>
              <select value={form.tier_id} onChange={(e) => set("tier_id", e.target.value)} className={inputCls} data-testid="new-appt-tier" disabled={!svc}>
                <option value="">Select…</option>
                {tiers.map((t) => <option key={t.id} value={t.id}>{t.label} — ${t.price} · {t.duration_minutes}min</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><p className={labelCls}>Date</p><input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="new-appt-date" /></div>
            <div><p className={labelCls}>Start time</p><input type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} className={inputCls} data-testid="new-appt-time" /></div>
          </div>
          <div><p className={labelCls}>Service address</p><input value={form.address} onChange={(e) => set("address", e.target.value)} className={inputCls} data-testid="new-appt-address" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><p className={labelCls}>Customer name</p><input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className={inputCls} data-testid="new-appt-name" /></div>
            <div><p className={labelCls}>Customer phone</p><input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} className={inputCls} data-testid="new-appt-phone" /></div>
          </div>
          <div><p className={labelCls}>Customer email</p><input type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} className={inputCls} data-testid="new-appt-email" /></div>
          <div>
            <p className={labelCls}>Deposit handling</p>
            <select value={form.deposit_handling} onChange={(e) => set("deposit_handling", e.target.value)} className={inputCls} data-testid="new-appt-deposit">
              <option value="pay_link">Email client a 50% deposit link (default)</option>
              <option value="collected">Deposit collected offline</option>
              <option value="waived">Waive deposit</option>
              <option value="paid_full">Mark as fully paid</option>
            </select>
          </div>
        </div>
        <div className="mt-5 flex gap-2">
          <button onClick={submit} disabled={saving} className="flex-1 bg-[#B87333] text-white rounded-full py-2.5 text-sm font-medium disabled:opacity-60" data-testid="new-appt-save">{saving ? "Creating…" : "Create appointment"}</button>
          <button onClick={onClose} className="px-5 border border-[#E6DEC3] rounded-full text-sm text-[#635B52]">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [view, setView] = useState("list");
  const [filter, setFilter] = useState("");
  const [sel, setSel] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = () => api.get("/admin/bookings", { params: filter ? { status: filter } : {} }).then((r) => setBookings(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]);

  const byDate = bookings.reduce((acc, b) => { (acc[b.date] = acc[b.date] || []).push(b); return acc; }, {});
  const dates = Object.keys(byDate).sort();

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex gap-1 bg-white border border-[#E6DEC3] rounded-full p-1">
          <button onClick={() => setView("list")} className={`inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-sm ${view === "list" ? "bg-[#2C2621] text-white" : "text-[#635B52]"}`} data-testid="view-list"><List size={14} /> List</button>
          <button onClick={() => setView("calendar")} className={`inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-sm ${view === "calendar" ? "bg-[#2C2621] text-white" : "text-[#635B52]"}`} data-testid="view-calendar"><Calendar size={14} /> Calendar</button>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-lg border border-[#E6DEC3] px-3 py-2 text-sm bg-white" data-testid="booking-filter">
            <option value="">All statuses</option>
            {["confirmed", "pending_approval", "rescheduled", "balance_due", "completed", "cancelled"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
          <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-1 bg-[#B87333] text-white rounded-full px-4 py-2 text-sm whitespace-nowrap" data-testid="new-appointment-btn"><Plus size={14} /> New Appointment</button>
        </div>
      </div>

      {view === "list" ? (
        <div className="bg-white rounded-2xl border border-[#E6DEC3] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F0E8] text-[#635B52]">
              <tr>{["Booking", "Service", "Date", "Time", "Total", "Status", "Customer"].map((h) => <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-[#E6DEC3]">
              {bookings.map((b) => (
                <tr key={b.booking_id} onClick={() => setSel(b)} className="hover:bg-[#FBF9F5] cursor-pointer" data-testid={`booking-row-${b.booking_number}`}>
                  <td className="px-4 py-3 font-medium text-[#B87333]">{b.booking_number}</td>
                  <td className="px-4 py-3 text-[#2C2621]">{b.service_name}</td>
                  <td className="px-4 py-3 text-[#635B52]">{b.date}</td>
                  <td className="px-4 py-3 text-[#635B52]">{b.start_time}</td>
                  <td className="px-4 py-3 text-[#2C2621]">${b.total_price.toFixed(2)}</td>
                  <td className="px-4 py-3"><Badge s={b.status} /></td>
                  <td className="px-4 py-3 text-[#635B52]">{b.customer_name}</td>
                </tr>
              ))}
              {!bookings.length && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#635B52]">No bookings.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-4" data-testid="calendar-view">
          {dates.map((d) => (
            <div key={d} className="bg-white rounded-2xl border border-[#E6DEC3] p-4">
              <p className="font-semibold text-[#2C2621] mb-2">{new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {byDate[d].sort((a, b) => a.start_time.localeCompare(b.start_time)).map((b) => (
                  <button key={b.booking_id} onClick={() => setSel(b)} className="text-left rounded-lg border border-[#E6DEC3] p-3 hover:border-[#B87333]/50" data-testid={`cal-booking-${b.booking_number}`}>
                    <div className="flex justify-between items-center"><span className="font-medium text-[#2C2621]">{b.start_time}</span><Badge s={b.status} /></div>
                    <p className="text-sm text-[#635B52] mt-1">{b.service_name} — {b.customer_name}</p>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {!dates.length && <p className="text-center text-[#635B52] py-10">No bookings.</p>}
        </div>
      )}

      {sel && <BookingModal b={sel} onClose={() => setSel(null)} onChanged={load} />}
      {showNew && <NewAppointmentModal onClose={() => setShowNew(false)} onCreated={load} />}
    </div>
  );
}
